import { FC } from "react";
import styled from "styled-components";

import { TranscriptionStatus } from "../../app/services/mediaTranscription";
import {
  hasTranscriptionPreview,
  TranscriptionCellInfo,
} from "../../hooks/useTranscriptionStatuses";
import StatusBadge, { StatusBadgeChipColor } from "../status-badge/StatusBadge";
import ToolTip from "../tooltip/ToolTip";

/**
 * 文字起こしステータスの一覧セル表示（チップ / 本文プレビュー）。
 *
 * 一覧（共有ライブラリ / トークルーム / メッセンジャー / ショット編集 /
 * 作品登録）で `useTranscriptionStatuses` の結果を表示する。
 * 未実行（`none` / undefined）は何も出さない。
 *
 * - pending / processing / failed: 状態チップ
 * - complete: 「文字起こし済み」チップの代わりに本文プレビューを 1 行表示。
 *   セル幅を超えるぶんは ellipsis（…）で省略し、ホバーで全文（先頭 200 文字）
 *   をツールチップ表示する。タイムスタンプ [M:SS] は載せない（モーダル側のみ）。
 *   プレビューが無い旧データは従来どおりチップにフォールバックする。
 *
 * 失敗は soremo-green（SOREMO では警告色として緑を使う）。
 */

type BadgeSpec = { label: string; color: StatusBadgeChipColor };

const BADGE_BY_STATUS: Partial<Record<TranscriptionStatus, BadgeSpec>> = {
  pending: { label: "文字起こし待機中", color: "light-gray" },
  processing: { label: "文字起こし中", color: "blue" },
  complete: { label: "文字起こし済み", color: "light-gray" },
  failed: { label: "文字起こし失敗", color: "green" },
};

/**
 * 本文プレビューの 1 行省略表示。flex 行内でファイル名の残り幅だけを使い、
 * 収まらないぶんは …（ellipsis）で切る（ファイル名側の縮小を横取りしない
 * よう flex-basis は 0 にする）。
 * フォントは「文字起こし済み」チップ（StatusBadge small）と同じ 8px。
 */
const PreviewText = styled.span.attrs({
  className: "u-text-light-gray",
})`
  flex: 1 1 0;
  min-width: 0;
  font-size: 8px;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`;

export interface TranscriptionStatusCellProps {
  entry?: TranscriptionCellInfo;
}

const TranscriptionStatusCell: FC<TranscriptionStatusCellProps> = ({
  entry,
}) => {
  if (!entry) return null;

  if (hasTranscriptionPreview(entry)) {
    return (
      <ToolTip description={entry.textPreview} truncatedOnly>
        <PreviewText>{entry.textPreview}</PreviewText>
      </ToolTip>
    );
  }

  const spec = BADGE_BY_STATUS[entry.status];
  if (!spec) return null;

  // サイズは一覧の行に並べる前提で small 固定（「共有中」チップと同じ）
  return (
    <StatusBadge
      label={spec.label}
      variant="chip"
      color={spec.color}
      size="small"
    />
  );
};

export default TranscriptionStatusCell;
